import { writable, get } from 'svelte/store';

import { Dispatcher } from './Dispatcher.js'; // --> circular
import { Mixer } from './Mixer.js';
import { BuildSettings } from './OutputConfig.js';
import { chatMessages } from './stores.js';

class Peer {

    constructor(type, id="", name="", channel_name="", can_render_tile = false){

        this.channel_name = channel_name; //aka roomName
        this.id = id;
        this.self_id = "", // -- this is weird but maybe for self_peer id is "self" and self_id is whatever id we get from signalling
        this.name = writable(name);
        this.type = type;
        this.is_self = false;
        
        this.ts = 0;
        
        this.can_render_tile = writable(can_render_tile);

        this.needsVStream = true;
        this.has_joined_session = false;
        this.use_expanded_io = writable(false);

        this.render_level = writable(0);
        this.render_routing = {
            0: writable(0),
            1: writable(1),
            2: writable(2),
            3: writable(3)
        }
    }   
}

export const Peers = writable([]);

export const Peers_Handler = {
    
    self_peer: null,

    _validate_id_format(id){
        const re = /[0-9a-f]{8}/g;
        return re.test(id);
    },

    create_self(channel_name, type){
        
        Peers_Handler.self_peer = new Peer(type);
        Peers_Handler.self_peer.is_self = true;
        Peers_Handler.self_peer.use_expanded_io.set((type === "eng"));
        Peers_Handler.self_peer.channel_name = channel_name;
        Peers_Handler.self_peer.id = "self";
        Peers_Handler.self_peer.ts = Date.now();
        Peers.update(peers => [...peers, Peers_Handler.self_peer]);
        //console.log(`[PL-Peers] Created Self ${type}, ${Peers_Handler.self_peer.id}`);

    },

    set_self_id(id){
        
        console.assert(Peers_Handler.self_peer);
        console.assert(this._validate_id_format(id));
        
        Peers_Handler.self_peer.id = id;
    },

    /*
     Remove ** remote ** peer
    */
    remove_remote_peer(id){
        
        Peers.update(peers => {
            console.log(`[PL-Peers] Removing peer ${id}`);
            return peers.filter(p => p.id !== id);
        })

        // -- check if we can disable video encoding
        if (this.self_peer.type === "eng"){
            const disable_video_decoding = get(Peers).every(p => p.needsVStream === false);
            BuildSettings.ENABLE_VSTREAM_SWITCH && Dispatcher.decoderThread.postMessage({route: "disable_video_decoding", value: disable_video_decoding});
        };
    },

    add_remote_peers(remote_peers, room_name){

        Peers.update(peers => {

            // -- reconcile peers we already know: a post-reconnect roster carries the CURRENT
            // -- name/can_render_tile, and any set-display-name / notify-can-render broadcast
            // -- during our outage was lost. new-remote-peer payloads omit these fields
            // -- entirely (undefined), so live announcements never clobber known state.
            for (const rp of remote_peers){
                const existing = peers.find(p => p.id === rp.id);
                if (!existing) continue;
                if (rp.name !== undefined && get(existing.name) !== rp.name)
                    existing.name.set(rp.name);
                if (rp.can_render_tile !== undefined && get(existing.can_render_tile) !== rp.can_render_tile)
                    existing.can_render_tile.set(rp.can_render_tile);
            }

            const newPeers = remote_peers
                .filter(rp => !peers.some(p => p.id === rp.id))
                .map(rp => {
                    console.log(`[PL-Peers] Adding new peer id: ${rp.id}, type: ${rp.type}`);
                    return new Peer(rp.type, rp.id, rp.name, room_name, rp.can_render_tile);
                });
        
            if (newPeers.length === 0) {
                return peers; // No changes, no need to trigger update.
            }

            const allPeers = [...peers, ...newPeers];
        
            // -- sort them so the conference tiles in the UI have a specific ordering
            return allPeers.sort((a, b) => {
                const rank = (p) => {
                    if (p.type === "talent") return 0;
                    if (p.is_self) return 1;
                    if (p.type === "eng") return 2;
                    return 3;
                };
                return rank(a) - rank(b);
            });
        });
    },

    clear(){
        Peers.set([]);
    },

    
    set_chat_msg(msg){
        chatMessages.update(msgs => {
            !msgs.windowOpen && msgs.unread++;
            msgs.msgs.push(msg.payload);
            return msgs;
        })

    },

    notify_name_change(name){
        console.assert(Peers_Handler.self_peer);
        Peers_Handler.self_peer.name.set(name);
        Dispatcher.notifyNameChange(Peers_Handler.self_peer.id, name);
    },

    // -- receive from signaling
    update_remote_name(id, name){
        
        Peers.update(peers => {

            const peer = peers.filter(p => p.id === id)[0]
            if (peer)
                peer.name.set(name);

            return peers;
        })
    },

    set_can_render_tile(id, value = true){
        
        const peer = get(Peers).filter(p => p.id === id)[0];
        if (!peer){
            console.error("[PL Peers] No peer to set render flag on", id, value);
            return;
        }

        peer.can_render_tile.set(value);
        
        if (peer.is_self)
            Dispatcher.notifyCanRender(Peers_Handler.self_peer.id, value);
    },


    remove_peer_from_conf_mixer(id){
        Mixer.removeConfSource(id);
    },

    remove_peer_from_stream_mixer(id){
        Mixer.removeStreamSource(id);
    },

}
